import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronRight } from 'lucide-react';
import { NAV_ITEMS } from '@/lib/index';

const scrollToSection = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
};

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300
      ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-border' : 'bg-transparent'}`}>
      <div className="container mx-auto px-6 lg:px-10 max-w-7xl">
        <div className="flex items-center justify-between h-16 lg:h-[70px]">

          {/* Logo */}
          <button onClick={() => scrollToSection('hero')} className="flex items-center group">
            <div className={`transition-all duration-300 ${
              isScrolled
                ? 'bg-transparent px-0 py-0 rounded-none'
                : 'bg-white px-3 py-1.5 rounded-lg shadow-md'
            }`}>
              <img
                src="/images/ao_logo.png"
                alt="AO Consulting"
                className="h-8 w-auto object-contain"
              />
            </div>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <button key={item.href} onClick={() => scrollToSection(item.href)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
                  ${isScrolled
                    ? 'text-foreground/70 hover:text-foreground hover:bg-secondary'
                    : 'text-white/80 hover:text-white hover:bg-white/10'}`}>
                {item.label}
              </button>
            ))}
          </nav>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-3">
            <button onClick={() => scrollToSection('contact')}
              className="px-5 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-semibold
                         hover:bg-primary/90 transition-all duration-200 shadow-sm hover:shadow-md">
              Get in Touch
            </button>
          </div>

          {/* Mobile */}
          <button className={`md:hidden p-2 rounded-lg transition-colors
            ${isScrolled ? 'text-foreground hover:bg-secondary' : 'text-white hover:bg-white/10'}`}
            onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu">
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-border shadow-lg">
            <div className="container mx-auto px-6 py-4 flex flex-col gap-1">
              {NAV_ITEMS.map((item) => (
                <button key={item.href} onClick={() => { scrollToSection(item.href); setMobileOpen(false); }}
                  className="flex items-center justify-between text-foreground/80 hover:text-foreground
                             py-3 text-sm font-medium border-b border-border last:border-0">
                  {item.label} <ChevronRight size={14} className="text-muted-foreground" />
                </button>
              ))}
              <button onClick={() => { scrollToSection('contact'); setMobileOpen(false); }}
                className="mt-3 w-full py-3 bg-primary text-primary-foreground rounded-xl text-sm font-semibold">
                Get in Touch
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

export function Footer() {
  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="container mx-auto px-6 lg:px-10 max-w-7xl py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 pb-12 border-b border-white/10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2.5 mb-4">
              <img src="/images/ao_logo.png" alt="AO Consulting" className="h-7 w-auto object-contain brightness-0 invert" />
            </div>
            <p className="text-primary-foreground/55 text-sm leading-relaxed max-w-xs">
              Strategic advisory at the intersection of Banking, Fintech, and Business Growth.
              14+ years of progressive leadership across Nigeria's financial services industry.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-sm mb-5 text-accent uppercase tracking-wider">Navigate</h4>
            <ul className="space-y-3">
              {[...NAV_ITEMS, { label: 'Testimonials', href: 'testimonials' }].map((item) => (
                <li key={item.href}>
                  <button onClick={() => scrollToSection(item.href)}
                    className="text-primary-foreground/55 hover:text-white text-sm transition-colors">
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-sm mb-5 text-accent uppercase tracking-wider">Contact</h4>
            <ul className="space-y-3 text-sm">
              <li><a href="mailto:AOConsultingServ@gmail.com"
                className="text-primary-foreground/55 hover:text-white transition-colors text-xs break-all">
                AOConsultingServ@gmail.com
              </a></li>
              <li><a href="tel:+2348036872163"
                className="text-primary-foreground/55 hover:text-white transition-colors font-mono text-xs">
                +234 803 687 2163
              </a></li>
              <li className="text-primary-foreground/35 text-xs">Lagos, Nigeria</li>
            </ul>
          </div>
        </div>
        <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-primary-foreground/30">
          <p>&copy; {new Date().getFullYear()} AO Consulting. All rights reserved.</p>
          <p>Ayoola Olalekan · COO · 14+ Years Experience</p>
        </div>
      </div>
    </footer>
  );
}
